import {BreadcrumbType, DataStatus} from 'platform/components';
import {Show} from 'platform/foundation';

import {useLocation} from 'react-router-dom';

import {isNotNil} from 'ramda';

import {
  PurchasePriceRuleRequestBody,
  useCreatePurchasePriceRuleMutation,
  useListPurchasePriceRulesQuery,
} from '@dms/api/sales';
import {i18n} from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsTemplate} from '@dms/settings/shared';
import {usePermissions} from '@dms/shared';

import {PurchasePriceRuleForm} from '../../components/PurchasePriceRuleForm/PurchasePriceRuleForm';
import {PurchasePriceRuleNewLocationStateSchema} from '../../types/PurchasePriceRuleNewLocationStateSchema';
import {buildPurchasePriceRuleTemplate} from '../../utils/buildPurchasePriceRuleTemplate';

export function PurchasePriceRuleNew() {
  const location = useLocation();
  const [canManage] = usePermissions({permissionKeys: ['managePurchasePriceRules']});
  const [createPurchasePriceRule] = useCreatePurchasePriceRuleMutation();

  const locationState = PurchasePriceRuleNewLocationStateSchema.safeParse(location.state);
  const stateTemplate = locationState.success ? locationState.data.template : null;

  // A page refresh or a direct link carries no template, so the rule starts as a copy of the
  // default rule, the same as "Copy the default rule" in the new rule dialog.
  const {
    data: rules,
    isLoading,
    isError,
  } = useListPurchasePriceRulesQuery(undefined, {skip: !canManage || isNotNil(stateTemplate)});

  const template =
    stateTemplate ??
    (isNotNil(rules)
      ? buildPurchasePriceRuleTemplate({mode: 'COPY_DEFAULT', rules, sourceRuleId: null})
      : null);

  const breadcrumbs: BreadcrumbType[] = [
    {
      label: i18n.t('page.settings.purchasePrice.title'),
      href: settingsRoutes.purchasePrice,
    },
  ];

  const handleSave = (body: PurchasePriceRuleRequestBody) =>
    createPurchasePriceRule({purchasePriceRuleRequestBody: body}).unwrap();

  return (
    <SettingsTemplate
      header={{
        title: i18n.t('page.settings.purchasePrice.marginRules.newRule'),
        subtitle: i18n.t('page.settings.purchasePrice.ruleEditor.header.newSubtitle'),
        breadcrumbs,
      }}
      headerVariant="v2"
      isLoading={isLoading}
      isError={isError}
      data-testid={testIds.settings.purchasePriceRuleNew('page')}
    >
      <DataStatus
        isEmpty={!canManage}
        emptyMessage={i18n.t('general.notifications.noPermission')}
        data-testid={testIds.settings.purchasePriceRuleNew('noPermission')}
      >
        <Show when={template}>
          {(template) => (
            <PurchasePriceRuleForm
              source={{type: 'template', template}}
              isReadOnly={false}
              onSave={handleSave}
              baseTestId={testIds.settings.purchasePriceRuleNew}
            />
          )}
        </Show>
      </DataStatus>
    </SettingsTemplate>
  );
}
